"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import Image from "@/src/components/Image"
import { branchesAPI } from '@/src/lib/api/branches'
import { accountsAPI } from '@/src/lib/api/accounts'
import { toast } from 'sonner'
import type { BranchAccount } from '@/src/types/account'

interface BankAccount {
  id: string
  name: string
  accountNumber: string
  bankName: string
  status: string
  icon: string
  linkedBranchCount: number
  linkedBranches: Array<{ id: string; name: string }>
}

interface AddBranchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode?: "add" | "edit"
  branchId?: string
  initialData?: {
    branchName: string
    selectedAccounts: string[]
  }
}

export function AddBranchDialog({ 
  open, 
  onOpenChange, 
  mode = "add",
  branchId,
  initialData 
}: AddBranchDialogProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [branchName, setBranchName] = useState(initialData?.branchName || "")
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>(initialData?.selectedAccounts || [])
  const [bankAccounts, setBankAccounts] = useState<BranchAccount[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Load accounts when dialog opens
  useEffect(() => {
    if (open) {
      loadAccounts()
    }
  }, [open])

  const loadAccounts = async () => {
    try {
      setLoading(true)
      const accounts = await accountsAPI.getAll()
      setBankAccounts(accounts)
    } catch (error) {
      console.error('Failed to load accounts:', error)
      toast.error('ไม่สามารถโหลดรายการบัญชีได้')
    } finally {
      setLoading(false)
    }
  }

  // Map BranchAccount to BankAccount format for display
  const displayAccounts: BankAccount[] = bankAccounts.map(account => ({
    id: account.id.toString(),
    name: account.account_name,
    accountNumber: account.account_number || account.promptpay_id || account.shop_code || account.truemoney_id || '',
    bankName: account.bank_code ? `ธนาคาร${account.bank_code}` : account.account_source === 'promptpay' ? 'PromptPay' : account.account_source === 'shop' ? 'Shop' : 'TrueMoney',
    status: account.is_active ? 'ใช้งาน' : 'ไม่ใช้งาน',
    icon: account.bank_code ? `/icons/icon-${account.bank_code.toLowerCase()}.png` : '/icons/icon-bank.svg',
    linkedBranchCount: (account as any).linked_branch_count ?? 0,
    linkedBranches: (account as any).linked_branches ?? [],
  }))

  // Filter accounts based on search query
  const filteredAccounts = displayAccounts.filter(account =>
    account.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    account.bankName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    account.accountNumber.includes(searchQuery)
  )

  // Handle account selection toggle
  const toggleAccountSelection = (accountId: string) => {
    // Handle "all" selection
    if (accountId === "all") {
      if (selectedAccounts.includes("all")) {
        // Unselect all
        setSelectedAccounts([])
      } else {
        // Select all accounts
        setSelectedAccounts(bankAccounts.map(account => String(account.id)))
      }
    } else {
      setSelectedAccounts(prev => {
        const newSelection = prev.includes(accountId)
          ? prev.filter(id => id !== accountId)
          : [...prev, accountId]
        
        // Remove "all" if individual account is deselected
        return newSelection.filter(id => id !== "all")
      })
    }
  }

  // Handle confirm button click
  const handleConfirm = async () => {
    if (!branchName.trim()) {
      toast.error('กรุณากรอกชื่อสาขา')
      return
    }

    try {
      setSubmitting(true)
      
      const requestData = {
        branch_name: branchName,
        account_ids: selectedAccounts.filter(id => id !== 'all')
      }

      if (mode === 'edit' && branchId) {
        await branchesAPI.update(branchId, requestData)
        toast.success('แก้ไขสาขาสำเร็จ')
      } else {
        await branchesAPI.create(requestData)
        toast.success('เพิ่มสาขาสำเร็จ')
      }
      
      onOpenChange(false)
    } catch (error: any) {
      console.error('Failed to save branch:', error)
      toast.error(error.response?.data?.error || 'เกิดข้อผิดพลาดในการบันทึก')
    } finally {
      setSubmitting(false)
    }
  }

  // Reset form when dialog opens/closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setBranchName("")
      setSelectedAccounts([])
      setSearchQuery("")
    } else if (initialData) {
      setBranchName(initialData.branchName)
      setSelectedAccounts(initialData.selectedAccounts)
    }
    onOpenChange(open)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px] p-0 gap-0">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4">
          <DialogTitle className="text-xl font-semibold text-gray-900">
            {mode === "edit" ? "แก้ไขสาขา" : "เพิ่มสาขา"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {mode === "edit" ? "แก้ไขข้อมูลสาขาและเลือกบัญชีธนาคารที่ต้องการเชื่อมต่อ" : "สร้างสาขาใหม่และเลือกบัญชีธนาคารที่ต้องการเชื่อมต่อ"}
          </DialogDescription>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          {/* Branch Name Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              ชื่อสาขา
            </label>
            <Input
              placeholder="กรอกชื่อสาขา"
              className="w-full"
              value={branchName}
              onChange={(e) => setBranchName(e.target.value)}
            />
          </div>

          {/* Bank Accounts Selection */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-900">
                เลือกบัญชีของสาขานี้
              </label>
              <span className="text-sm font-medium">
                <span className="text-brand-light-red">
                  {selectedAccounts.length}
                </span>
                &nbsp;บัญชี
              </span>
            </div>

            {/* Bank Accounts List */}
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {filteredAccounts.map((account) => (
                <div
                  key={account.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center overflow-hidden">
                      <Image
                        src={account.icon}
                        alt={account.name}
                        width={44}
                        height={44}
                        onError={(e) => {
                          const img = e.currentTarget
                          if (!img.dataset.fallback) {
                            img.dataset.fallback = '1'
                            img.src = '/icons/icon-bank-circle.png'
                          }
                        }}
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-green-600">
                        {account.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {account.bankName}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {account.linkedBranchCount > 0 && (
                      <span
                        title={account.linkedBranches.map(b => b.name).join(', ')}
                        className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full whitespace-nowrap"
                      >
                        {account.linkedBranchCount} สาขา
                      </span>
                    )}
                    {account.bankName && (
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {account.status}
                        </p>
                        <p className="text-xs text-gray-500">
                          ตรวจทั้งหมด
                        </p>
                      </div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleAccountSelection(account.id)}
                      className={`min-w-[60px] rounded-2xl ${
                        selectedAccounts.includes(account.id)
                          ? "bg-brand-primary text-white"
                          : "bg-gray-200 text-gray-600 border-transparent"
                      }`}
                    >
                      เลือก
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="grid grid-cols-3 items-center justify-between gap-3 px-6 py-4">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
          >
            ยกเลิก
          </Button>
          <div></div>
          <Button
            onClick={handleConfirm}
            variant="default"
            disabled={submitting || loading}
          >
            {submitting ? 'กำลังบันทึก...' : mode === "edit" ? "บันทึก" : "ยืนยัน"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
